import { useCallback, useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, KanbanSquare, CalendarClock, FileText,
    FolderKanban, Activity, Sun, Moon, LogOut, Search, Zap, ChevronRight, Bell, Radar, Settings,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { uniqueFollowups } from "@/lib/notificationUtils";
import { NAV, DASHBOARD, NAV_EXTRA } from "@/constants/testIds/crm";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NAV_ITEMS = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: NAV.dashboard, end: true },
    { to: "/leads", label: "Leads", icon: Users, testId: NAV.leads },
    { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testId: NAV.pipeline },
    { to: "/contacts", label: "Contacts", icon: Users, testId: NAV.contacts },
    { to: "/clients", label: "Clients", icon: FolderKanban, testId: NAV.clients },
    { to: "/invoices", label: "Invoices", icon: FileText, testId: NAV.invoices },
    { to: "/expenses", label: "Expenses", icon: FileText, testId: "nav-expenses" },
    { to: "/project-pricing", label: "Project Pricing", icon: CalendarClock, testId: "nav-project-pricing" },
    { to: "/discovery", label: "Lead Discovery", icon: Radar, testId: NAV.discovery },
    { to: "/followups", label: "Follow-ups", icon: CalendarClock, testId: NAV.followups },
    { to: "/proposals", label: "Proposals", icon: FileText, testId: NAV.proposals },
    { to: "/projects", label: "Projects", icon: FolderKanban, testId: NAV.projects },
    { to: "/activities", label: "Activity Log", icon: Activity, testId: NAV.activities },
];

const ADMIN_NAV_ITEMS = [
    { to: "/settings/followup-automation", label: "Follow-up Settings", icon: Settings, testId: NAV_EXTRA.settings },
    { to: "/users", label: "Users", icon: Users, testId: NAV.users },
];

function ThemeToggle() {
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
    useEffect(() => {
        document.documentElement.classList.toggle("dark", dark);
        localStorage.setItem("zbz-theme", dark ? "dark" : "light");
    }, [dark]);
    return (
        <button
            type="button"
            onClick={() => setDark((d) => !d)}
            data-testid={NAV.themeToggle}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-foreground hover:bg-secondary transition-colors"
            aria-label="Toggle theme"
        >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
    );
}

function GlobalSearch() {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        if (q.length < 2) { setResults(null); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
                setResults(data);
                setOpen(true);
            } catch { /* noop */ }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        function onDoc(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const goto = (path) => { setOpen(false); setQ(""); nav(path); };

    return (
        <div className="relative w-full max-w-md" ref={boxRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
                data-testid={NAV.globalSearch}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => results && setOpen(true)}
                placeholder="Search leads, projects, clients…"
                className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-card text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
            />
            {open && results && (
                <div className="absolute z-40 mt-2 w-full rounded-md border border-border bg-popover shadow-lg overflow-hidden">
                    {["leads", "contacts", "projects", "clients", "invoices"].map((k) => (
                        results[k]?.length > 0 && (
                            <div key={k} className="p-2">
                                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground px-2 pb-1">{k}</div>
                                {results[k].slice(0, 5).map((it) => (
                                    <button
                                        key={it.id}
                                        data-testid={NAV.globalSearchResult}
                                        onClick={() => goto(k === "projects" ? "/projects" : k === "clients" ? "/clients" : k === "contacts" ? "/contacts" : k === "invoices" ? "/invoices" : "/leads")}
                                        className="w-full text-left px-2 py-1.5 rounded hover:bg-secondary text-sm flex items-center justify-between transition-colors"
                                    >
                                        <span className="truncate">{it.company || it.name || it.client_name || it.invoice_number}</span>
                                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                    </button>
                                ))}
                            </div>
                        )
                    ))}
                    {!results.leads?.length && !results.projects?.length && !results.clients?.length && !results.contacts?.length && !results.invoices?.length && (
                        <div className="px-4 py-6 text-sm text-muted-foreground text-center">No results.</div>
                    )}
                </div>
            )}
        </div>
    );
}

function NotificationBell() {
    const [counts, setCounts] = useState({ today: 0, overdue: 0 });
    const [items, setItems] = useState({ followups: [], overdue: [] });
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/dashboard/today?filter=today");
            setCounts(data.counts || { today: 0, overdue: 0 });
            setItems({
                followups: Array.isArray(data.followups) ? data.followups : [],
                overdue: Array.isArray(data.overdue) ? data.overdue : [],
            });
        } catch {
            setCounts({ today: 0, overdue: 0 });
            setItems({ followups: [], overdue: [] });
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, [load]);

    const total = (counts.today || 0) + (counts.overdue || 0);
    const rows = uniqueFollowups([
        ...(items.overdue || []).map((fu) => ({ ...fu, __kind: "overdue" })),
        ...(items.followups || []).map((fu) => ({ ...fu, __kind: "followup" })),
    ]).slice(0, 10);

    return (
        <Popover open={open} onOpenChange={(next) => { setOpen(next); if (next) load(); }}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={DASHBOARD.notifBell}
                    className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-foreground hover:bg-secondary transition-colors"
                    aria-label="Notifications"
                >
                    <Bell className="h-4 w-4" />
                    {total > 0 && (
                        <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${
                            counts.overdue > 0 ? "bg-destructive text-white" : "bg-primary text-primary-foreground"
                        }`}>
                            {total > 99 ? "99+" : total}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={10} className="w-[360px] p-0">
                <div className="border-b border-border px-4 py-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Notifications</div>
                    <div className="text-sm font-medium mt-1">Follow-ups due today and overdue</div>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                    {rows.length === 0 ? (
                        <div className="px-4 py-8 text-sm text-muted-foreground text-center">No notifications right now.</div>
                    ) : (
                        rows.map((fu) => {
                            const lead = fu.lead || {};
                            const isOverdue = fu.__kind === "overdue";
                            return (
                                <button
                                    key={`${fu.__kind}-${fu.id}`}
                                    type="button"
                                    onClick={() => { window.location.href = `/leads?lead=${lead.id}`; setOpen(false); }}
                                    className="w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                                    isOverdue ? "bg-destructive/15 text-destructive" : "bg-primary/10 text-primary"
                                                }`}>
                                                    {isOverdue ? "Overdue" : "Due"}
                                                </span>
                                                <span className="text-xs font-semibold truncate">{lead.company || "Lead"}</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1 truncate">
                                                {fu.stage_name || fu.type} · {lead.client_name || "No client name"}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                                                {new Date(fu.date).toLocaleString()}
                                            </div>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
                <div className="border-t border-border px-4 py-3 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                        {counts.today || 0} due today · {counts.overdue || 0} overdue
                    </span>
                    <button
                        type="button"
                        onClick={() => { setOpen(false); window.location.href = "/"; }}
                        className="text-xs text-primary hover:underline"
                    >
                        Open dashboard
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default function Layout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

    return (
        <div className="min-h-screen bg-background text-foreground flex">
            {/* Sidebar */}
            <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card">
                <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
                    <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                        <Zap className="h-4 w-4" strokeWidth={2.5} />
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className="font-heading text-lg font-bold tracking-tight">Zyberzing</span>
                        <span className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Sales CRM</span>
                    </div>
                </div>
                <nav className="flex-1 p-3 space-y-0.5">
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            data-testid={item.testId}
                            className={({ isActive }) =>
                                `sidebar-link flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                                    isActive
                                        ? "bg-secondary text-foreground font-medium"
                                        : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                                }`
                            }
                        >
                            <item.icon className="h-4 w-4" />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                    {user?.role === "admin" && (
                        <>
                            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground/70 px-3 pt-4 pb-1">
                                Admin
                            </div>
                            {ADMIN_NAV_ITEMS.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={item.testId}
                                    className={({ isActive }) =>
                                        `sidebar-link flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                                            isActive
                                                ? "bg-secondary text-foreground font-medium"
                                                : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                                        }`
                                    }
                                >
                                    <item.icon className="h-4 w-4" />
                                    <span>{item.label}</span>
                                </NavLink>
                            ))}
                        </>
                    )}
                </nav>
                <div className="p-3 border-t border-border">
                    <div className="flex items-center gap-3 p-2 rounded-md bg-secondary/60">
                        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">
                            {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{user?.name}</div>
                            <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{user?.role?.replace("_", " ")}</div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="sticky top-0 z-30 h-16 border-b border-border bg-background/80 backdrop-blur-xl flex items-center px-4 md:px-8 gap-4">
                    <div className="lg:hidden font-heading font-bold text-lg">Zyberzing</div>
                    <div className="flex-1"><GlobalSearch /></div>
                    <NotificationBell />
                    <ThemeToggle />
                    <button
                        data-testid={NAV.logoutButton}
                        onClick={async () => { await logout(); nav("/login"); }}
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-card text-sm hover:bg-secondary transition-colors"
                        aria-label="Logout"
                    >
                        <LogOut className="h-4 w-4" />
                        <span className="hidden md:inline">Logout</span>
                    </button>
                </header>
                <main className="flex-1 p-4 md:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
